import React, { useMemo, useState } from "react";
import { X, Trash2 } from "lucide-react";

/**
 * Modal d'effacement sélectif de l'horaire.
 *
 * Permet d'effacer :
 *  - Tout l'horaire
 *  - Parties d'une ou plusieurs date(s)
 *  - Parties d'une ou plusieurs surface(s)
 *  - Combinaison (ex: date 2026-06-01 + surface 1)
 *
 * Props:
 *  - games: array — liste actuelle
 *  - onClose()
 *  - onConfirm(gamesToKeep: array)
 */
export default function ClearScheduleModal({ games, onClose, onConfirm }) {
  const allDates = useMemo(
    () => [...new Set(games.map((g) => g.date).filter(Boolean))].sort(),
    [games]
  );
  const allSurfaces = useMemo(
    () => [...new Set(games.map((g) => String(g.surface || "")).filter(Boolean))].sort(),
    [games]
  );

  const [scope, setScope] = useState("all"); // all | partial
  const [selDates, setSelDates] = useState([]);
  const [selSurfaces, setSelSurfaces] = useState([]);
  const [includeBreaks, setIncludeBreaks] = useState(true);

  const toggle = (arr, setArr, value) => {
    if (arr.includes(value)) setArr(arr.filter((x) => x !== value));
    else setArr([...arr, value]);
  };

  const matchesDelete = (g) => {
    if (scope === "all") {
      if (!includeBreaks && g.is_break) return false;
      return true;
    }
    // partial : must match at least one filter actually selected
    const dateOk = selDates.length === 0 || selDates.includes(g.date || "");
    const surfaceOk = selSurfaces.length === 0 || selSurfaces.includes(String(g.surface || ""));
    // si AUCUN filtre n'est sélectionné en mode partial → ne rien effacer (sécurité)
    if (selDates.length === 0 && selSurfaces.length === 0) return false;
    // Combinaison ET : si les deux filtres sont actifs il faut matcher les deux
    if (!dateOk || !surfaceOk) return false;
    if (!includeBreaks && g.is_break) return false;
    return true;
  };

  const willDelete = games.filter(matchesDelete);
  const willKeep = games.filter((g) => !matchesDelete(g));
  const deleteCount = willDelete.length;
  const deleteAssignments = willDelete.filter(
    (g) => !g.is_break && (g.referee1_id || g.referee2_id || g.scorer_id)
  ).length;
  const partialNoSelection =
    scope === "partial" && selDates.length === 0 && selSurfaces.length === 0;

  const handleConfirm = () => {
    if (deleteCount === 0) return;
    onConfirm(willKeep);
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="clear-schedule-modal"
    >
      <div
        className="rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-[var(--surface-3)]"
          aria-label="Fermer"
          data-testid="clear-modal-close"
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        <div className="label mb-1">Effacement sélectif</div>
        <h3 className="text-xl font-bold tracking-tight mb-4">Quelles parties veux-tu effacer?</h3>

        {/* Scope toggle */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            type="button"
            onClick={() => setScope("all")}
            className={`p-3 rounded-xl border-2 text-left transition-colors ${
              scope === "all" ? "border-black bg-black/5" : "border-[var(--border-strong)] hover:border-black"
            }`}
            data-testid="clear-scope-all"
          >
            <div className="font-bold text-sm">Tout l'horaire</div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Toutes parties + pauses</div>
          </button>
          <button
            type="button"
            onClick={() => setScope("partial")}
            className={`p-3 rounded-xl border-2 text-left transition-colors ${
              scope === "partial" ? "border-black bg-black/5" : "border-[var(--border-strong)] hover:border-black"
            }`}
            data-testid="clear-scope-partial"
          >
            <div className="font-bold text-sm">Effacement ciblé</div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Par date et/ou surface</div>
          </button>
        </div>

        {scope === "partial" && (
          <div className="space-y-4 mb-4">
            {allDates.length > 0 && (
              <div>
                <div className="label mb-2">Date(s)</div>
                <div className="flex flex-wrap gap-2">
                  {allDates.map((d) => {
                    const active = selDates.includes(d);
                    const count = games.filter((g) => g.date === d).length;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggle(selDates, setSelDates, d)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium tabular border transition-colors ${
                          active
                            ? "bg-black text-white border-black"
                            : "bg-transparent text-[var(--text)] border-[var(--border-strong)] hover:bg-[var(--surface-3)]"
                        }`}
                        data-testid={`clear-date-${d}`}
                      >
                        {d} <span className="opacity-60">({count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {allSurfaces.length > 0 && (
              <div>
                <div className="label mb-2">Surface(s)</div>
                <div className="flex flex-wrap gap-2">
                  {allSurfaces.map((s) => {
                    const active = selSurfaces.includes(s);
                    const count = games.filter((g) => String(g.surface || "") === s).length;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggle(selSurfaces, setSelSurfaces, s)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium tabular border transition-colors ${
                          active
                            ? "bg-black text-white border-black"
                            : "bg-transparent text-[var(--text)] border-[var(--border-strong)] hover:bg-[var(--surface-3)]"
                        }`}
                        data-testid={`clear-surface-${s}`}
                      >
                        Surface {s} <span className="opacity-60">({count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {selDates.length > 0 && selSurfaces.length > 0 && (
              <p className="text-xs text-[var(--muted)] leading-relaxed">
                ℹ Les parties devront matcher <strong>AU MOINS une</strong> date sélectionnée <strong>ET</strong> une surface sélectionnée pour être effacées.
              </p>
            )}
          </div>
        )}

        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={includeBreaks}
            onChange={(e) => setIncludeBreaks(e.target.checked)}
            className="w-3.5 h-3.5"
            data-testid="clear-include-breaks"
          />
          <span className="text-sm">Effacer aussi les pauses concernées</span>
        </label>

        {/* Preview */}
        <div
          className="rounded-xl p-4 mb-4"
          style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}
        >
          {partialNoSelection ? (
            <p className="text-sm text-[var(--muted)]">
              Sélectionne au moins une date ou une surface pour activer l'effacement.
            </p>
          ) : (
            <>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-2xl font-bold tabular" data-testid="clear-preview-count">
                  {deleteCount}
                </span>
                <span className="text-sm text-[var(--muted)]">
                  entrée{deleteCount > 1 ? "s" : ""} sera{deleteCount > 1 ? "ont" : ""} effacée{deleteCount > 1 ? "s" : ""}
                </span>
              </div>
              <div className="text-xs text-[var(--muted)]">
                {willKeep.length} entrée{willKeep.length > 1 ? "s" : ""} conservée{willKeep.length > 1 ? "s" : ""}
                {deleteAssignments > 0 && (
                  <>
                    {" "}·{" "}
                    <span className="text-[var(--danger)] font-semibold">
                      {deleteAssignments} assignation{deleteAssignments > 1 ? "s" : ""} perdue{deleteAssignments > 1 ? "s" : ""}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="clear-cancel-btn">
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            className="btn-danger"
            disabled={deleteCount === 0}
            data-testid="clear-confirm-btn"
          >
            <Trash2 size={14} strokeWidth={2.5} /> Effacer {deleteCount > 0 ? `(${deleteCount})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
